/**
 * Phone-Call Leads tab for the GHL Qualifier Location Detail page.
 *
 * Surfaces the form/phone-call job's contacts (ghl_form_call_lead_checks). This
 * is the primary contact feed for phone-call-only locations, whose qualified
 * and converted jobs are suppressed.
 */

import { useMemo } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { PhoneCallLeadsTable } from './PhoneCallLeadsTable'
import { QualifierPagination } from './QualifierPagination'
import type { PhoneCallLeadFeedRow } from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly data: readonly PhoneCallLeadFeedRow[]
  readonly count: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly statusFilter: string
  readonly searchTerm: string
  readonly onPageChange: (page: number) => void
  readonly onStatusChange: (status: string) => void
  readonly onSearchChange: (term: string) => void
  readonly onRowClick: (id: string) => void
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
] as const

export function PhoneCallLeadsTab({
  data,
  count,
  page,
  pageSize,
  isLoading,
  statusFilter,
  searchTerm,
  onPageChange,
  onStatusChange,
  onSearchChange,
  onRowClick,
}: Props) {
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return data
    return data.filter((row) => {
      const haystack = [
        row.contact_name,
        row.contact_email,
        row.contact_phone,
        row.contact_id,
        row.opportunity_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [data, searchTerm])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={statusFilter} onValueChange={onStatusChange}>
            <SelectTrigger className="h-9 w-[180px]" aria-label="Filter by status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">Auto-refreshing every 15 seconds</p>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search contacts"
            className="pl-9"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search contacts"
          />
        </div>
      </div>

      <PhoneCallLeadsTable data={filtered} isLoading={isLoading} onRowClick={onRowClick} />

      <QualifierPagination
        page={page}
        pageSize={pageSize}
        count={count}
        onPageChange={onPageChange}
      />
    </div>
  )
}
